import "./polyfills/screen-orientation";
import { createApp } from "vue";
import axios from "axios";
import Quasar from 'quasar/src/vue-plugin.js';import Dialog from 'quasar/src/plugins/dialog/Dialog.js';import Notify from 'quasar/src/plugins/notify/Notify.js';
import * as All from "quasar";
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import "../scss/app.scss";

import { ref, onMounted, reactive } from "vue";
import dayjs from "dayjs";
import RichTextEditor from "./components/RichTextEditor.vue";
import AdditionalInfoEditor from "./components/AdditionalInfoEditor.vue";
import CookieBanner from "./components/CookieBanner.vue";
import DatepickerWrapper from "./components/DatepickerWrapper.vue";
import InputWrapper from "./components/InputWrapper.vue";
import SelectWrapper from "./components/SelectWrapper.vue";
import SliderWrapper from "./components/SliderWrapper.vue";
import MultiSelectWrapper from "./components/MultiSelectWrapper.vue";
import CheckboxWrapper from "./components/CheckboxWrapper.vue";
import ColorPickerWrapper from "./components/ColorPickerWrapper.vue";
import StatisticsFilterForm from "./components/StatisticsFilterForm.vue";
import IconPickerWrapper from "./components/IconPickerWrapper.vue";
import BannerWrapper from "./components/BannerWrapper.vue";
import NavigationDrawer from "./components/NavigationDrawer.vue";
import UserSelectGroup from "./components/UserSelectGroup.vue";
import PublishersTable from "./components/PublishersTable.vue";
import DevPublishersTable from "./components/DevPublishersTable.vue";
import DevCalendarsTable from "./components/DevCalendarsTable.vue";
import DevSecurityIpsTable from "./components/DevSecurityIpsTable.vue";
import DevTrialSubscriptionsTable from "./components/DevTrialSubscriptionsTable.vue";
import DevUserSelect from "./components/DevUserSelect.vue";
import HelpIcon from "./components/HelpIcon.vue";
import WeekplanEditor from "./components/WeekplanEditor.vue";
import OnboardingWorkflowEditor from "./components/OnboardingWorkflowEditor.vue";
import UserOnboardingProcess from "./components/UserOnboardingProcess.vue";
import OnboardingTrackingTable from "./components/OnboardingTrackingTable.vue";
import VideoPlayer from "./components/VideoPlayer.vue";
import VideoUploader from "./components/VideoUploader.vue";
import CoachMarksTour from "./components/CoachMarksTour.vue";
import AiHelpChat from "./components/AiHelpChat.vue";
import CoordinatePicker from "./components/CoordinatePicker.vue";
import MapViewer from "./components/MapViewer.vue";
import RouteDrawer from "./components/RouteDrawer.vue";
import PaddleCheckout from "./components/PaddleCheckout.vue";
import SubscriptionAssistant from "./components/SubscriptionAssistant.vue";
import CampaignRulesEditor from "./components/CampaignRulesEditor.vue";
import ShiftInfiniteScroll from "./components/ShiftInfiniteScroll.vue";
import ShiftBlock from "./components/ShiftBlock.vue";
import { initializeSortable } from "./sortable-handler";
import { useItemReservation } from "./composables/useItemReservation";

import {
    browserSupportsWebAuthn as originalBrowserSupportsWebAuthn,
    startAuthentication,
    startRegistration,
} from "@simplewebauthn/browser";

const safelySupportsWebAuthn = () => {
    try {
        if (typeof window === "undefined") return false;
        if (
            window.isAppClient ||
            (navigator && navigator.userAgent && navigator.userAgent.includes("pw-tools-app"))
        ) {
            return false;
        }
        if (typeof globalThis.PublicKeyCredential === "undefined") {
            return false;
        }
        return originalBrowserSupportsWebAuthn();
    } catch (e) {
        return false;
    }
};

window.browserSupportsWebAuthn = safelySupportsWebAuthn;
window.startAuthentication = startAuthentication;
window.startRegistration = startRegistration;

window.translations = reactive(window.translations || {});
window.timesTranslations = reactive(window.timesTranslations || {});

// Frontend Error Tracking
const reportedErrors = new Set();
const sendFrontendError = (payload) => {
    try {
        if (!payload) return;
        const errorKey = `${payload.message}:${payload.url}:${payload.line}:${payload.col}`;
        if (reportedErrors.has(errorKey)) return;
        reportedErrors.add(errorKey);
        if (reportedErrors.size > 50) {
            const firstKey = reportedErrors.values().next().value;
            reportedErrors.delete(firstKey);
        }

        const data = {
            message: String(payload.message || "Unknown JS Error").substring(0, 2000),
            url: payload.url || window.location.href,
            line: payload.line || null,
            col: payload.col || null,
            stack: payload.stack ? String(payload.stack).substring(0, 10000) : null,
            component_info: payload.component_info ? String(payload.component_info).substring(0, 1000) : null,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
            type: payload.type || "js_exception",
        };

        let sent = false;
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            try {
                const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
                sent = navigator.sendBeacon("/api/frontend-errors", blob);
            } catch (e) {
                sent = false;
            }
        }
        if (!sent) {
            axios.post("/api/frontend-errors", data).catch(() => {});
        }
    } catch (e) {
        // Ignore errors in error logger itself
    }
};

if (typeof window !== "undefined") {
    window.addEventListener("error", function (event) {
        const overlay = document.getElementById("initial-loading-overlay");
        if (overlay) {
            overlay.style.display = "none";
        }

        sendFrontendError({
            message: event.message || (event.error && event.error.message),
            url: event.filename || window.location.href,
            line: event.lineno,
            col: event.colno,
            stack: event.error ? event.error.stack : null,
            type: "uncaught_error",
        });
    });

    window.addEventListener("unhandledrejection", function (event) {
        const reason = event.reason;
        const message = reason instanceof Error ? reason.message : String(reason || "Unhandled Promise Rejection");
        const stack = reason instanceof Error ? reason.stack : null;

        sendFrontendError({
            message: message,
            url: window.location.href,
            stack: stack,
            type: "unhandled_rejection",
        });
    });
}

const app = createApp({
    setup() {
        const $q = useQuasar();
        let leftNavigation = ref(false);
        const modals = reactive({});
        const formState = reactive({});

        const { startTime, endTime, startTimeOptions, endTimeOptions, update } =
            useItemReservation();

        const handleDeleteConfirm = (evt, title, message) => {
            const form = evt.target.closest("form");
            $q.dialog({
                title: title,
                message: message,
                cancel: true,
                persistent: true,
            }).onOk(() => {
                form.submit();
            });
        };

        const navigateDay = (dateString) => {
            // dateString is DD.MM.YYYY
            const [day, month, year] = dateString.split(".");
            location.href = `/tagesansicht/${year}/${month}/${day}`;
            if (modals["myModalToDayview"]) modals["myModalToDayview"] = false;
        };

        const navigateWeek = (dateString) => {
            const [day, month, year] = dateString.split(".");
            location.href = `/wochenansicht/${year}/${month}/${day}`;
            if (modals["myModalToWeekview"])
                modals["myModalToWeekview"] = false;
        };

        onMounted(() => {
            const overlay = document.getElementById("initial-loading-overlay");
            if (overlay) {
                overlay.style.display = "none";
            }

            try {
                initializeSortable("sortable", {
                    handle: ".drag-handle",
                    animation: 150,
                    onEnd: function (evt) {
                        var rows = evt.to.querySelectorAll("tr.item");
                        rows.forEach(function (row, index) {
                            var input = row.querySelector(".positionInput");
                            if (input) {
                                input.value = index + 1;
                            }
                        });
                    },
                });
            } catch (e) {
                console.warn("Sortable initialization warning:", e);
            }

            axios
                .get("/api/translations")
                .then((response) => {
                    const data = response.data;
                    window.monthNames = data.monthNames;
                    window.monthNamesShort = data.monthNamesShort;
                    window.dayNames = data.dayNames;
                    window.dayNamesMin = data.dayNamesMin;
                    Object.assign(window.translations, data.translations);
                    Object.assign(
                        window.timesTranslations,
                        data.timesTranslations,
                    );
                    document.dispatchEvent(
                        new CustomEvent("translations-loaded"),
                    );
                })
                .catch((error) => {
                    console.error("Error loading translations:", error);
                });

            safelyDisableSwipeRefresh();

            if (typeof window.Android !== "undefined" && typeof window.Android.getFbmToken === "function") {
                try {
                    const notificationToken = window.Android.getFbmToken();
                    const deviceType = typeof window.Android.getDeviceType === "function" ? window.Android.getDeviceType() : "Android";
                    const deviceName = typeof window.Android.getDeviceName === "function" ? window.Android.getDeviceName() : "";
                    axios
                        .post(
                            window.saveFbmTokenUrl ||
                                "/user/saveFirebaseMessagingClientToken",
                            {
                                notificationToken: notificationToken,
                                deviceType: deviceType,
                                deviceName: deviceName,
                            },
                        )
                        .then((response) => {
                            // good
                        })
                        .catch((error) => {
                            console.error(error);
                        });
                } catch (error) {
                    console.error(error);
                }
            }

            // Auto-open confirmation modal if present
            modals["confirmationModal"] = true;

            if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
                try {
                    navigator.serviceWorker
                        .getRegistrations()
                        .then(function (registrations) {
                            for (let registration of registrations) {
                                registration
                                    .unregister()
                                    .catch((err) => {
                                        console.warn("Service worker unregister error:", err);
                                    });
                            }
                        })
                        .catch((err) => {
                            console.warn("Service worker getRegistrations error:", err);
                        });
                } catch (err) {
                    console.warn("Service worker access not allowed:", err);
                }
            }

            if (window.isAppClient) {
                document.addEventListener("click", (event) => {
                    const link = event.target.closest("a");
                    if (link) {
                        const url = link.getAttribute("href");
                        if (
                            url &&
                            (url.includes("/file-download/") ||
                                link.classList.contains(
                                    "open-in-native-browser",
                                ))
                        ) {
                            link.classList.add("noLoadingOverlay");
                            event.preventDefault();

                            if (url.includes("/file-download/")) {
                                const parts = url.split("/file-download/");
                                if (parts.length >= 2) {
                                    const identifier = parts[1];
                                    fetch(`/file-download-token/${identifier}`)
                                        .then((response) => {
                                            if (!response.ok) {
                                                throw new Error("Unauthorized");
                                            }
                                            return response.json();
                                        })
                                        .then((data) => {
                                            const signedUrl = data.url;
                                            if (
                                                typeof window.Android !==
                                                    "undefined" &&
                                                typeof window.Android
                                                    .openExternal === "function"
                                            ) {
                                                window.Android.openExternal(
                                                    signedUrl,
                                                );
                                            } else {
                                                window.open(
                                                    signedUrl,
                                                    "_blank",
                                                );
                                            }
                                        })
                                        .catch((err) => {
                                            console.error(
                                                "Download authorization failed:",
                                                err,
                                            );
                                        });
                                }
                            } else {
                                if (
                                    typeof window.Android !== "undefined" &&
                                    typeof window.Android.openExternal ===
                                        "function"
                                ) {
                                    window.Android.openExternal(url);
                                } else {
                                    window.open(url, "_blank");
                                }
                            }
                        }
                    }
                });
            }

            if (window.matchMedia("(display-mode: standalone)").matches) {
                document.addEventListener("click", (event) => {
                    const link = event.target.closest("a");
                    if (link) {
                        const href = link.getAttribute("href");
                        if (
                            href &&
                            href.length &&
                            href !== "#" &&
                            href !== "javascript:void(0)" &&
                            link.target !== "_blank" &&
                            !href.includes("/file-download/") &&
                            !link.classList.contains("noLoadingOverlay") &&
                            !link.hasAttribute("data-no-loading-overlay") &&
                            !link.classList.contains(
                                "open-in-native-browser",
                            ) &&
                            !link.closest("li.has-dropdown")
                        ) {
                            const overlay = document.getElementById(
                                "initial-loading-overlay",
                            );
                            if (overlay) {
                                overlay.style.display = "flex";
                            }
                        }
                    }
                });
            }

            // Restore from bfcache (back-forward cache)
            window.addEventListener("pageshow", (event) => {
                if (event.persisted) {
                    const overlay = document.getElementById(
                        "initial-loading-overlay",
                    );
                    if (overlay) {
                        overlay.style.display = "none";
                    }

                    document
                        .querySelectorAll('[data-disabled-by-overlay="true"]')
                        .forEach((btn) => {
                            btn.disabled = false;
                            delete btn.dataset.disabledByOverlay;
                        });
                }
            });

            // Global form submission handler
            document.addEventListener("submit", (event) => {
                if (event.defaultPrevented) return;

                const form = event.target;
                if (form.target === "_blank") return;
                if (form.hasAttribute("data-no-loading-overlay")) return;
                if (
                    event.submitter &&
                    event.submitter.hasAttribute("data-no-loading-overlay")
                )
                    return;

                const overlay = document.getElementById(
                    "initial-loading-overlay",
                );
                if (overlay) {
                    overlay.style.display = "flex";
                }

                if (event.submitter) {
                    const submitter = event.submitter;
                    setTimeout(() => {
                        submitter.disabled = true;
                        submitter.dataset.disabledByOverlay = "true";
                    }, 0);
                }
            });
        });

        const copyToClipboard = (text) => {
            if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard
                    .writeText(text)
                    .then(() => {
                        $q.notify({
                            message:
                                window.translations["notification.link_copied"] ||
                                "Link copied to clipboard",
                            color: "positive",
                            icon: "check",
                            timeout: 2000,
                        });
                    })
                    .catch((err) => {
                        fallbackCopyText(text);
                    });
            } else {
                fallbackCopyText(text);
            }
        };

        const fallbackCopyText = (text) => {
            try {
                const textarea = document.createElement("textarea");
                textarea.value = text;
                textarea.style.position = "fixed";
                textarea.style.opacity = "0";
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
                $q.notify({
                    message:
                        window.translations["notification.link_copied"] ||
                        "Link copied to clipboard",
                    color: "positive",
                    icon: "check",
                    timeout: 2000,
                });
            } catch (err) {
                console.error("Could not copy text: ", err);
            }
        };

        // Global properties for template access
        app.config.globalProperties.$modals = modals;
        app.config.globalProperties.$navigateDay = navigateDay;
        app.config.globalProperties.$navigateWeek = navigateWeek;
        app.config.globalProperties.$copyToClipboard = copyToClipboard;
        app.config.globalProperties.$formState = formState;
        app.config.globalProperties.$window = window;
        app.config.globalProperties.$t = (key, replacements = {}) => {
            let translation = window.translations?.[key] || key;
            if (replacements && typeof replacements === "object") {
                Object.keys(replacements).forEach((param) => {
                    const val =
                        replacements[param] !== undefined &&
                        replacements[param] !== null
                            ? replacements[param]
                            : "";
                    translation = translation
                        .replace(new RegExp(`\\{${param}\\}`, "g"), val)
                        .replace(new RegExp(`:${param}\\b`, "g"), val);
                });
            }
            return translation;
        };

        app.config.errorHandler = (err, instance, info) => {
            console.error("Vue Runtime Error:", err, info);
            sendFrontendError({
                message: err ? (err.message || String(err)) : "Vue Error",
                url: window.location.href,
                stack: err ? err.stack : null,
                component_info: info,
                type: "vue_error",
            });
        };

        app.config.globalProperties.window = window;
        return {
            leftNavigation,
            modals,
            navigateDay,
            navigateWeek,
            copyToClipboard,
            handleDeleteConfirm,
            formState,
            startTime,

            endTime,
            startTimeOptions,
            endTimeOptions,
            update,
        };
    },
});

// Android SwipeRefresh integration helper
const getAndroid = () => {
    if (typeof window !== "undefined" && typeof window.Android !== "undefined") {
        return window.Android;
    }
    return null;
};

const safelyDisableSwipeRefresh = () => {
    try {
        const android = getAndroid();
        if (android && typeof android.setSwipeRefreshEnabled === "function") {
            android.setSwipeRefreshEnabled(false);
        }
    } catch (e) {
        console.error(
            "Error disabling Android swipe refresh on page open:",
            e,
        );
    }
};

// Run immediately and on various page open events to ensure it is failsafe
safelyDisableSwipeRefresh();
if (typeof window !== "undefined") {
    window.addEventListener("DOMContentLoaded", safelyDisableSwipeRefresh);
    window.addEventListener("load", safelyDisableSwipeRefresh);
    window.addEventListener("pageshow", safelyDisableSwipeRefresh);

    // Failsafe polling in case the WebView injects the interface late
    let checkAttempts = 0;
    const maxAttempts = 30; // 3 seconds total
    const intervalId = setInterval(() => {
        const android = getAndroid();
        if (android && typeof android.setSwipeRefreshEnabled === "function") {
            safelyDisableSwipeRefresh();
            clearInterval(intervalId);
        }
        checkAttempts++;
        if (checkAttempts >= maxAttempts) {
            clearInterval(intervalId);
        }
    }, 100);
}

const activeOverlays = new Set();

window.disableSwipeRefresh = function (instance) {
    if (instance) {
        activeOverlays.add(instance);
    }
    const android = getAndroid();
    if (android && typeof android.setSwipeRefreshEnabled === "function") {
        try {
            android.setSwipeRefreshEnabled(false);
        } catch (e) {
            console.error("Error disabling swipe refresh:", e);
        }
    }
};

window.enableSwipeRefresh = function (instance) {
    if (instance) {
        activeOverlays.delete(instance);
    }
    if (activeOverlays.size === 0) {
        const android = getAndroid();
        if (android && typeof android.setSwipeRefreshEnabled === "function") {
            try {
                android.setSwipeRefreshEnabled(true);
            } catch (e) {
                console.error("Error enabling swipe refresh:", e);
            }
        }
    }
};

app.mixin({
    created() {
        const instance = this.$;
        if (!instance) return;
        const originalEmit = instance.emit;
        if (originalEmit) {
            instance.emit = (event, ...args) => {
                const name = this.$options.name;
                if (
                    name === "QSelect" ||
                    name === "QDialog" ||
                    name === "QMenu" ||
                    name === "QDrawer"
                ) {
                    if (event === "popup-show" || event === "show") {
                        window.disableSwipeRefresh(instance);
                    } else if (event === "popup-hide" || event === "hide") {
                        window.enableSwipeRefresh(instance);
                    }
                }
                return originalEmit.call(instance, event, ...args);
            };
        }
    },
    beforeUnmount() {
        const instance = this.$;
        if (instance) {
            const name = this.$options?.name;
            if (
                name === "QSelect" ||
                name === "QDialog" ||
                name === "QMenu" ||
                name === "QDrawer"
            ) {
                window.enableSwipeRefresh(instance);
            }
        }
    },
});

app.directive("init", {
    beforeMount(el, binding, vnode) {
        // v-init is used to initialize state from PHP variables in Blade templates
    },
});

app.component("rich-text-editor", RichTextEditor);
app.component("additional-info-editor", AdditionalInfoEditor);
app.component("cookie-banner", CookieBanner);
app.component("datepicker-wrapper", DatepickerWrapper);
app.component("input-wrapper", InputWrapper);
app.component("select-wrapper", SelectWrapper);
app.component("slider-wrapper", SliderWrapper);
app.component("multi-select-wrapper", MultiSelectWrapper);
app.component("checkbox-wrapper", CheckboxWrapper);
app.component("statistics-filter-form", StatisticsFilterForm);
app.component("color-picker-wrapper", ColorPickerWrapper);
app.component("icon-picker-wrapper", IconPickerWrapper);
app.component("banner-wrapper", BannerWrapper);
app.component("navigation-drawer", NavigationDrawer);
app.component("user-select-group", UserSelectGroup);
app.component("publishers-table", PublishersTable);
app.component("dev-publishers-table", DevPublishersTable);
app.component("dev-calendars-table", DevCalendarsTable);
app.component("dev-security-ips-table", DevSecurityIpsTable);
app.component("dev-trial-subscriptions-table", DevTrialSubscriptionsTable);
app.component("dev-user-select", DevUserSelect);
app.component("help-icon", HelpIcon);
app.component("weekplan-editor", WeekplanEditor);
app.component("onboarding-workflow-editor", OnboardingWorkflowEditor);
app.component("user-onboarding-process", UserOnboardingProcess);
app.component("onboarding-tracking-table", OnboardingTrackingTable);
app.component("video-player", VideoPlayer);
app.component("video-uploader", VideoUploader);
app.component("coach-marks-tour", CoachMarksTour);
app.component("ai-help-chat", AiHelpChat);
app.component("coordinate-picker", CoordinatePicker);
app.component("map-viewer", MapViewer);
app.component("route-drawer", RouteDrawer);
app.component("paddle-checkout", PaddleCheckout);
app.component("subscription-assistant", SubscriptionAssistant);
app.component("campaign-rules-editor", CampaignRulesEditor);
app.component("shift-infinite-scroll", ShiftInfiniteScroll);
app.component("shift-block", ShiftBlock);

const startApp = async () => {
    const locale = window.locale || "en_US";

    const localeMap = {
        ar_AE: { quasar: "ar", dayjs: "ar" },
        ar: { quasar: "ar", dayjs: "ar" },
        da_DA: { quasar: "da", dayjs: "da" },
        da: { quasar: "da", dayjs: "da" },
        de_DE: { quasar: "de", dayjs: "de" },
        de: { quasar: "de", dayjs: "de" },
        el_GR: { quasar: "el", dayjs: "el" },
        el: { quasar: "el", dayjs: "el" },
        en_US: { quasar: "en-US", dayjs: "en" },
        en: { quasar: "en-US", dayjs: "en" },
        es_ES: { quasar: "es", dayjs: "es" },
        es: { quasar: "es", dayjs: "es" },
        fi_FI: { quasar: "fi", dayjs: "fi" },
        fi: { quasar: "fi", dayjs: "fi" },
        fr_FR: { quasar: "fr", dayjs: "fr" },
        fr: { quasar: "fr", dayjs: "fr" },
        hr_HR: { quasar: "hr", dayjs: "hr" },
        hr: { quasar: "hr", dayjs: "hr" },
        hu_HU: { quasar: "hu", dayjs: "hu" },
        hu: { quasar: "hu", dayjs: "hu" },
        it_IT: { quasar: "it", dayjs: "it" },
        it: { quasar: "it", dayjs: "it" },
        nl_NL: { quasar: "nl", dayjs: "nl" },
        nl: { quasar: "nl", dayjs: "nl" },
        pl_PL: { quasar: "pl", dayjs: "pl" },
        pl: { quasar: "pl", dayjs: "pl" },
        pt_BR: { quasar: "pt-BR", dayjs: "pt-br" },
        pt_PT: { quasar: "pt", dayjs: "pt" },
        pt: { quasar: "pt", dayjs: "pt" },
        ro_RO: { quasar: "ro", dayjs: "ro" },
        ro: { quasar: "ro", dayjs: "ro" },
        ru_RU: { quasar: "ru", dayjs: "ru" },
        ru: { quasar: "ru", dayjs: "ru" },
        sv_SE: { quasar: "sv", dayjs: "sv" },
        sv: { quasar: "sv", dayjs: "sv" },
        uk_UA: { quasar: "uk", dayjs: "uk" },
        uk: { quasar: "uk", dayjs: "uk" },
        zh_TW: { quasar: "zh-TW", dayjs: "zh-tw" },
        zh: { quasar: "zh-CN", dayjs: "zh-cn" },
    };

    const config = localeMap[locale] || { quasar: "en-US", dayjs: "en" };

    let langPack = undefined;
    try {
        const lang = await import(
            `../../node_modules/quasar/lang/${config.quasar}.js`
        );
        langPack = lang ? lang.default : undefined;
    } catch (e) {
        console.warn(`Could not load Quasar lang pack for ${config.quasar}`, e);
    }

    try {
        await import(`../../node_modules/dayjs/locale/${config.dayjs}.js`);
        dayjs.locale(config.dayjs);
    } catch (e) {
        console.warn(`Could not load Dayjs locale for ${config.dayjs}`, e);
    }

    const quasarOptions = {
        config: {},
        components: All,
        directives: All,
        plugins: {
            Dialog,
            Notify,
        },
        cssAddon: true,
        extras: [],
    };
    if (langPack) {
        quasarOptions.lang = langPack;
    }

    app.use(Quasar, quasarOptions);

    const vm = app.mount("#app");
    window.vm = vm;
    document.dispatchEvent(new CustomEvent("vue-mounted"));

    // Dynamically import ALTCHA after Vue mounts to prevent pre-mount custom element upgrade/duplication
    import("altcha").catch((e) => {
        console.warn("ALTCHA dynamic import failed:", e);
    });
};

startApp().catch((err) => {
    console.error("Failed to start app:", err);
    sendFrontendError({
        message: err ? (err.message || String(err)) : "Failed to start app",
        url: window.location.href,
        stack: err ? err.stack : null,
        type: "app_start_error",
    });
});

import "../scss/app.scss";
